import { Lock, RefreshCw, Sparkles } from "lucide-react";
import { trialDaysLeft, trialEnded } from "../lib/access";
import { reloadToLatest } from "../lib/update";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Link } from "../lib/router";
import { ReceiptCameraLink } from "../components/ReceiptCameraLink";
import { LogoMark } from "../components/Logo";
import { AdCard } from "../components/AdCard";
import { Mini, type Area } from "../components/Tactile";
import { LegalLinks } from "./legal/LegalLinks";
import "./home/home.css";

/**
 * Home is a launcher and nothing else: a greeting, the trial line, and every part of the app as a pressable
 * tile. Each part's own numbers (list, budget, pantry…) live on its own page, so home stays calm.
 */
export default function HomePage() {
  const { t, lang } = useI18n();
  const { me } = useAuth();
  const plus = !!me?.access.plus;

  const tiles: { to: string; area: Area; title: string; free?: boolean; receipt?: boolean }[] = [
    { to: "/shopping", area: "shop", title: t("home.shopping"), free: true },
    { to: "/wallet", area: "wallet", title: t("home.cards"), free: true },
    { to: "/ledger/new?scan=1", area: "receipt", title: t("home.receipt"), receipt: true },
    { to: "/ledger", area: "ledger", title: t("home.ledger") },
    { to: "/meals", area: "food", title: t("home.meals") },
    { to: "/fridge", area: "fridge", title: t("home.fridge") },
    { to: "/goods", area: "goods", title: t("home.goods") },
    { to: "/report", area: "report", title: t("report.home") },
    { to: "/places", area: "places", title: t("home.places"), free: true },
    { to: "/board", area: "board", title: t("home.board"), free: true },
    { to: "/me/plans", area: "plus", title: t("home.plus"), free: true },
    { to: "/me", area: "me", title: t("nav.me"), free: true },
  ];
  const daysLeft = me ? trialDaysLeft(me.access) : null;
  const ended = me ? trialEnded(me.access) : false;

  return (
    <main className="page home">
      <div className="home-top">
        <span className="brand">
          <span className="brand-mark">
            <LogoMark size={22} />
          </span>
          NutriCart
        </span>
        {/* Refresh: reloads with the newest version (pulling down does the same). */}
        <button type="button" className="round-btn" onClick={reloadToLatest} aria-label={t("common.refresh")} title={t("common.refresh")}>
          <RefreshCw size={20} strokeWidth={2.6} />
        </button>
      </div>

      <div className="home-hello">
        <span className="home-date">
          {new Intl.DateTimeFormat(lang === "zh" ? "zh-CN" : lang, { weekday: "long", day: "numeric", month: "long" }).format(new Date())}
        </span>
        <h1>{me?.displayName ? t("home.hello", { name: me.displayName }) : t("home.helloAnon")}</h1>
      </div>

      {daysLeft !== null && (
        <Link to="/me/plans" className="home-note">
          <Sparkles size={18} aria-hidden="true" />
          <span className="grow">{daysLeft <= 1 ? t("trial.bannerLast") : t("trial.banner", { days: daysLeft })}</span>
          <span className="home-note-cta">{t("trial.bannerCta")} →</span>
        </Link>
      )}
      {ended && (
        <Link to="/me/plans" className="home-note warn">
          <Lock size={18} aria-hidden="true" />
          <span className="grow">{t("trial.endedBanner")}</span>
          <span className="home-note-cta">{t("trial.endedCta")} →</span>
        </Link>
      )}

      <section className="stack" style={{ gap: 14 }} aria-labelledby="home-all">
        <h2 id="home-all" className="home-h2">
          {t("home.all")}
        </h2>
        <nav className="app-tiles" aria-labelledby="home-all">
          {tiles.map((x) => {
            const face = (
              <>
                <span className="app-tile-face">
                  <Mini area={x.area} size="lg" />
                  {x.free && x.area !== "plus" && x.area !== "me" && <span className="app-tile-free">{t("free.badge")}</span>}
                  {!plus && !x.free && (
                    <span className="app-tile-lock" aria-label={t("home.locked")}>
                      <Lock size={11} strokeWidth={3} />
                    </span>
                  )}
                </span>
                <strong>{x.title}</strong>
              </>
            );
            return x.receipt ? (
              <ReceiptCameraLink key={x.to} to={x.to} enabled={plus} className="app-tile">
                {face}
              </ReceiptCameraLink>
            ) : (
              <Link key={x.to} to={x.to} className="app-tile">
                {face}
              </Link>
            );
          })}
        </nav>
        <p className="home-free">{t("home.freeLine")}</p>
      </section>

      <AdCard placement="home" />
      <LegalLinks />
    </main>
  );
}
